import { ContractError, IncentivesApiError } from './types'
import type { AnalyticsOverview, Notification, ResponseTypes, RewardRecord } from './types'
import { requestBody } from './api'
import type { ReadEndpoint, ReadQueries, ReadRequester } from './api'

type ReadData = ResponseTypes[ReadEndpoint]
export type AnalyticsSnapshotBinding = Pick<AnalyticsOverview, 'subject_id' | 'projection_revision' | 'range' | 'as_of'>
export interface ResourceState {
  endpoint: ReadEndpoint
  queryKey: string
  data: ReadData | null
  loading: boolean
  error: { status: number; code: string; retryAfter: string | null } | null
}
// Display data can change without a new financial policy revision. Invalidate once per release.
export const DISPLAY_REVISION = 'test-display-20261020-v1'
export interface IncentivesState {
  displayRevision?: string
  identity: string
  generation: number
  locale: string
  subject: string | null
  rewardEngine: 'legacy' | 'v2' | null
  confirmedV2Subjects: string[]
  revisions: Record<string, number>
  resources: Record<string, ResourceState>
}
const dictionary = <T>(): Record<string, T> => Object.create(null)
export function initialState(): IncentivesState {
  return { identity: '', generation: 0, locale: '', subject: null, rewardEngine: null, confirmedV2Subjects: [], revisions: dictionary<number>(), resources: dictionary<ResourceState>() }
}
function stableKey(query: Record<string, unknown>) {
  return JSON.stringify(Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined).sort(([a], [b]) => a.localeCompare(b))))
}
function queryKey(query: Record<string, unknown>) {
  const { cursor: _cursor, ...filter } = query
  return stableKey(filter)
}
function errorState(error: unknown): ResourceState['error'] {
  return error instanceof IncentivesApiError ? { status: error.status, code: error.errorCode, retryAfter: error.retryAfter }
    : { status: error instanceof ContractError ? 502 : 0, code: error instanceof ContractError ? 'invalid_contract' : 'network_error', retryAfter: null }
}
/** All private resources are scoped to one verified auth session generation, never a guessed owner id. */
export function createIncentivesController(state: IncentivesState, request: ReadRequester) {
  const flights = new Map<string, { key: string; controller: AbortController; promise: Promise<ReadData | undefined> }>()
  const abortAll = () => { flights.forEach(f => f.controller.abort()); flights.clear() }
  function setSession(identity: string, locale: string) {
    if (state.identity === identity && state.locale === locale && state.displayRevision === DISPLAY_REVISION) return
    abortAll()
    const changedIdentity = state.identity !== identity
    state.displayRevision = DISPLAY_REVISION
    state.identity = identity
    state.locale = locale
    state.generation += 1
    state.resources = dictionary<ResourceState>()
    // A locale refresh cannot change the established canonical identity or erase revision floors.
    if (changedIdentity) { state.subject = null; state.rewardEngine = null; state.confirmedV2Subjects = []; state.revisions = dictionary<number>() }
  }
  function invalidateIdentity(slot: string, endpoint: ReadEndpoint, queryKey: string, error: ResourceState['error'], preserveV2 = false) {
    abortAll()
    state.generation += 1
    if (!preserveV2) { state.subject = null; state.rewardEngine = null; state.revisions = dictionary<number>() }
    state.resources = dictionary<ResourceState>()
    state.resources[slot] = { endpoint, queryKey, data: null, loading: false, error }
  }
  function cancel(slot: string) {
    const flight = flights.get(slot)
    if (flight) { flight.controller.abort(); flights.delete(slot) }
    if (state.resources[slot]) state.resources[slot].loading = false
  }
  function clear(slot: string) { cancel(slot); delete state.resources[slot] }
  function load<E extends ReadEndpoint>(slot: string, endpoint: E, query: ReadQueries[E], options: { append?: boolean; force?: boolean; analyticsSnapshot?: AnalyticsSnapshotBinding } = {}): Promise<ResponseTypes[E] | undefined> {
    if (!state.identity) return Promise.resolve(undefined)
    const body = requestBody(endpoint, { ...query, lang: state.locale } as ReadQueries[E])
    const { schema_version: _schema, ...capturedQuery } = body
    const binding = options.analyticsSnapshot ? { subject_id: options.analyticsSnapshot.subject_id, projection_revision: options.analyticsSnapshot.projection_revision, as_of: options.analyticsSnapshot.as_of, range: { ...options.analyticsSnapshot.range } } : null
    const key = queryKey(body), fullKey = `${endpoint}:${stableKey(body)}:${binding ? JSON.stringify(binding) : ''}`
    const previous = state.resources[slot]
    const active = flights.get(slot)
    if (active?.key === fullKey && !options.force) return active.promise as Promise<ResponseTypes[E] | undefined>
    const same = previous?.endpoint === endpoint && previous.queryKey === key
    if (options.append) {
      if (!same || !previous.data || !('page' in previous.data) || !body.cursor || body.cursor !== previous.data.page.next_cursor) return Promise.reject(new IncentivesApiError(400, 'invalid_cursor'))
    } else if (body.cursor) return Promise.reject(new IncentivesApiError(400, 'invalid_cursor'))
    // A content_cursor is a bounded single-task query, not the task-list cursor. The caller keeps a separate slot.
    cancel(slot)
    if (!same) state.resources[slot] = { endpoint, queryKey: key, data: null, loading: false, error: null }
    const resource = state.resources[slot]
    resource.loading = true
    resource.error = null
    const generation = state.generation, identity = state.identity, controller = new AbortController()
    const current = () => !controller.signal.aborted && generation === state.generation && identity === state.identity && state.resources[slot] === resource && flights.get(slot)?.controller === controller
    const flight = { key: fullKey, controller, promise: Promise.resolve(undefined) as Promise<ReadData | undefined> }
    flights.set(slot, flight)
    flight.promise = Promise.resolve().then(() => {
      if (!current()) throw new IncentivesApiError(401, 'stale_session')
      return request(endpoint, capturedQuery as ReadQueries[E], { identity, generation, signal: controller.signal })
    }).then(data => {
      if (!current()) return undefined
      if (endpoint === 'initialize' && (state.rewardEngine !== 'v2' || previous?.data && 'scope_id' in previous.data && 'scope_id' in data && previous.data.scope_id !== data.scope_id) && data.reward_engine === 'v2' && 'transition' in data && data.transition?.state === 'ready' && (state.subject === null || state.subject === data.subject_id)) {
        // A verified initialize result is the sole intentional legacy→V2 transition.
        for (const [other, pending] of flights) if (other !== slot) { pending.controller.abort(); flights.delete(other) }
        state.resources = Object.assign(dictionary<ResourceState>(), { [slot]: resource })
        state.revisions = dictionary<number>()
        state.rewardEngine = 'v2'
      }
      if (data.reward_engine === 'legacy' && state.confirmedV2Subjects.includes(data.subject_id)) {
        // Once this canonical identity confirms v2, a contradictory response must never unlock legacy read-awards.
        state.subject = data.subject_id
        state.rewardEngine = 'v2'
        invalidateIdentity(slot, endpoint, key, { status: 409, code: 'reward_engine_conflict', retryAfter: null }, true)
        return undefined
      }
      // Even a migration conflict for the established subject permanently closes legacy awards for this session.
      if (data.reward_engine === 'v2' && data.subject_id === state.subject && state.rewardEngine === 'legacy' && !state.confirmedV2Subjects.includes(data.subject_id)) state.confirmedV2Subjects.push(data.subject_id)
      if (state.subject !== null && data.subject_id !== state.subject || state.rewardEngine !== null && data.reward_engine !== state.rewardEngine) {
        invalidateIdentity(slot, endpoint, key, { status: 409, code: 'identity_changed', retryAfter: null })
        return undefined
      }
      // Validate the first page and exact-detail response before exposing any row to Vue.
      if (endpoint === 'creator/analytics/contents' && binding && 'projection_revision' in data && (data.subject_id !== binding.subject_id || data.projection_revision !== binding.projection_revision || data.as_of !== binding.as_of || data.range.key !== binding.range.key || data.range.start_at !== binding.range.start_at || data.range.end_at !== binding.range.end_at)) throw new IncentivesApiError(409, 'analytics_snapshot_expired')
      if ('state_revision' in data && data.state_revision < (state.revisions[data.scope_id] ?? -1)) throw new IncentivesApiError(409, 'stale_state')
      if (options.append && resource.data) {
        if ('scope_id' in data && 'scope_id' in resource.data && data.scope_id !== resource.data.scope_id) throw new IncentivesApiError(409, 'scope_changed')
        if ('projection_revision' in data && 'projection_revision' in resource.data && (data.projection_revision !== resource.data.projection_revision || data.as_of !== resource.data.as_of || JSON.stringify(data.range) !== JSON.stringify(resource.data.range))) throw new IncentivesApiError(409, 'analytics_snapshot_expired')
      }
      state.subject = data.subject_id
      state.rewardEngine = data.reward_engine
      if (data.reward_engine === 'v2' && !state.confirmedV2Subjects.includes(data.subject_id)) state.confirmedV2Subjects.push(data.subject_id)
      if ('state_revision' in data) state.revisions[data.scope_id] = data.state_revision
      if (options.append && resource.data && 'list' in resource.data && 'list' in data) {
        const rowKey = (row: any): string => row.id ?? row.content_id
        const rows = new Map<string, any>(resource.data.list.map((row: any) => [rowKey(row), row]))
        data.list.forEach((row: any) => rows.set(rowKey(row), row))
        resource.data = { ...data, list: [...rows.values()] } as ReadData
      } else resource.data = data
      return resource.data
    }).catch(error => {
      if (current()) {
        resource.error = errorState(error)
        if (error instanceof IncentivesApiError && [401, 403].includes(error.status)) {
          invalidateIdentity(slot, endpoint, key, resource.error)
          return undefined
        }
        // An expired snapshot/privacy change must stop showing previously public content immediately.
        if (error instanceof IncentivesApiError && error.errorCode === 'analytics_snapshot_expired') resource.data = null
      }
      return undefined
    }).finally(() => { if (current()) { resource.loading = false; flights.delete(slot) } })
    return flight.promise as Promise<ResponseTypes[E] | undefined>
  }
  const invalidate = (code = 'identity_changed', status = 409) => invalidateIdentity('incentives:overview', 'overview', '', { status, code, retryAfter: null })
  return { state, setSession, load, clear, cancel, invalidate, dispose: abortAll }
}
/** Only the seen CAS winner may present facts already resolved from the bounded receipt lookup. */
export function presentableNotifications(notifications: Notification[], records: RewardRecord[], newlySeenIds: string[]): Notification[] {
  const winner = new Set(newlySeenIds), byId = new Map(records.map(record => [record.id, record]))
  return notifications.filter(notification => winner.has(notification.id) && notification.receipt_ids.every(id => {
    const record = byId.get(id)
    return record && record.notification_policy !== 'silent' && record.rule_id !== 'welcome'
  }) && (notification.receipt_ids.length > 0 || notification.kind === 'badge_earned' && notification.badge_keys.length > 0))
}
